import React from 'react';
import PropTypes from 'prop-types';
import {createAnimatedComponent} from 'animated';

import AnimatedLetterMask from './animated-letter-mask.js';

import GalleryConfig from '../assets/img/gallery/index.js';

const AnimatedPath = createAnimatedComponent('path');
const AnimatedPolygon = createAnimatedComponent('polygon');

const animationConfigs = {
    'S': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 53]
        },
        translateX: {
            inputRange: [0, 0.15, 1],
            outputRange: [0, 18, 18]
        },
        translateY: {
            inputRange: [0, 0.15, 1],
            outputRange: [0, 25, 25]
        },
        rotation: {
            inputRange: [0, 0.25, 1],
            outputRange: [0, 0, 55]
        }
    },
    'H': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 47]
        },
        translateX: {
            inputRange: [0, 1],
            outputRange: [0, -30]
        }
    },
    'I': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 36]
        },
        rotation: {
            inputRange: [0, 1],
            outputRange: [0, 90]
        }
    },
    'V': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 49]
        },
        rotation: {
            inputRange: [0, 1],
            outputRange: [0, 90]
        },
        translateY: {
            inputRange: [0, 1],
            outputRange: [0, 30]
        }
    },
    'A': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 40]
        },
        rotation: {
            inputRange: [0, 1],
            outputRange: [0, -75]
        },
        translateY: {
            inputRange: [0, 1],
            outputRange: [0, 25]
        }
    },
    'D': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 42]
        },
        translateX: {
            inputRange: [0, 0.5, 1],
            outputRange: [0, -40, -45]
        }
    },
    'E': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 40]
        }
    },
    'SS': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 63]
        },
        rotation: {
            inputRange: [0, 1],
            outputRange: [0, -55]
        }
    },
    'II': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 36]
        },
        rotation: {
            inputRange: [0, 1],
            outputRange: [0, -90]
        }
    },
    'G': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 36]
        },
        translateY: {
            inputRange: [0, 1],
            outputRange: [0, -40]
        },
        rotation: {
            inputRange: [0, 1],
            outputRange: [0, -90]
        }
    },
    'N': {
        scale: {
            inputRange: [0, 1],
            outputRange: [1, 50]
        },
        translateY: {
            inputRange: [0, 1],
            outputRange: [0, 5]
        },
        rotation: {
            inputRange: [0, 1],
            outputRange: [0, -45]
        }
    },
};

const viewboxWidth = 2000;

export default class AnimatedLetters extends React.PureComponent {
    static propTypes = {
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        onSelectLetter: PropTypes.func.isRequired
    };

    state = {
        active: false,
        selected: false
    };

    baseLetterProps = (clipId) => ({
        clipId,
        key: clipId,
        imageWidth: viewboxWidth,
        imageHeight: (viewboxWidth / this.props.width) * this.props.height,
        startY: (viewboxWidth / this.props.width) * this.props.height / 2 - (180/2),
        image: GalleryConfig[clipId][0],
        animationConfig: animationConfigs[clipId],
        onAnimationStarted: this.animationStarted,
        onAnimationEnded: this.props.onSelectLetter,
        active: this.state.active === clipId
    })

    animationStarted = (clipId) => this.setState({active: clipId});

    renderSPath() {
        return [
            <AnimatedPath key="s-path-1" d="M64.75,39.7503 L64.75,0.2503 C64.75,0.2503 116.75,-4.9997 122.75,53.0003 L81.5,59.0003 C81.5,59.0003 82.75,39.5003 64.75,39.7503 Z"/>,
            <AnimatedPath key="s-path-2" d="M52,179.1668 L52,138.9998 C52,138.9998 41.833,137.4998 41.833,118.6668 L0,126.4998 C0,126.4998 1.833,178.4998 52,179.1668"/>,
            <AnimatedPath key="s-path-3" d="M64.8335,139.1668 L64.8335,179.3338 C64.8335,179.3338 107.1775,183.6958 120.4845,144.3468 C124.6265,132.0978 125.0905,118.8338 121.7085,106.3538 C118.9585,96.2088 113.1665,84.5838 101.0005,78.6668 C81.5155,69.1908 65.0225,66.9828 59.2235,66.4788 C56.6285,66.2528 54.0635,65.7138 51.6455,64.7458 C47.1085,62.9268 41.3335,59.2798 41.3335,52.4998 C41.3335,41.3338 52.0005,40.3338 52.0005,40.3338 L52.0005,0.1668 C52.0005,0.1668 20.6495,-1.5122 6.4035,28.4408 C-0.8385,43.6678 -1.0695,61.4318 5.7005,76.8748 C10.6115,88.0788 19.7365,99.8338 36.6665,103.6668 L71.1665,109.6668 C71.1665,109.6668 82.6665,110.8338 82.6665,124.8338 C82.6665,138.8338 72.6665,139.1668 72.6665,139.1668 C72.6665,139.1668 64.8335,139.3338 64.8335,139.1668"/>
        ];
    }

    renderIPath() {
        return <AnimatedPolygon points="0 180 42 180 42 0 0 0" />;
    }

    renderS() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('S')}
                maskWidth={124} maskHeight={180}
                backgroundX={-30} backgroundY={150}
                startX={250}>
                {this.renderSPath()}
            </AnimatedLetterMask>
        );
    }

    renderH() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('H')}
                maskWidth={126} maskHeight={180}
                backgroundX={30} backgroundScale={0.5}
                startX={394}>
                <AnimatedPolygon points="0.504 179.497207 42.84 179.497207 42.84 0 0.504 0" />
                <AnimatedPolygon points="83.664 179.497207 126 179.497207 126 0 83.664 0" />
                <AnimatedPolygon points="55.799856 113.132514 119.766528 113.132514 119.766528 70.8938547 55.799856 70.8938547" />
            </AnimatedLetterMask>
        );
    }

    renderI() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('I')}
                maskWidth={42} maskHeight={180}
                backgroundScale={0.15}
                startX={540}>
                {this.renderIPath()}
            </AnimatedLetterMask>
        );
    }

    renderV() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('V')}
                maskWidth={156} maskHeight={180}
                startX={602}
                backgroundX={-120} backgroundY={-50} backgroundScale={0.4}>
                <AnimatedPolygon points="99.6387097 179.497508 57.3677419 179.497508 0.503225806 0.000301675978 42.7741935 0.000301675978" />
                <AnimatedPolygon points="84.5419355 85.9779553 106.180645 154.357844 156 0.000301675978 113.729032 0.000301675978" />
            </AnimatedLetterMask>
        );
    }

    renderA() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('A')}
                maskWidth={155} maskHeight={180}
                startX={740}
                backgroundX={-50} backgroundY={-40} backgroundScale={0.3}>
                <AnimatedPolygon points="42.6083929 179.497508 0.335665584 179.497508 45.6278734 31.173486 68.4420617 95.1961676" />
                <AnimatedPolygon points="155 179.497508 112.727273 179.497508 50.8279221 0.000301675978 93.1006494 0.000301675978" />
                <AnimatedPolygon points="97.2942045 114.972536 76.1578409 114.972536 63.4085877 157.207173 110.37862 157.207173" />
            </AnimatedLetterMask>
        );
    }

    renderD() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('D')}
                maskWidth={124} maskHeight={180}
                startX={970}
                backgroundX={-35} backgroundScale={0.15}>
                <AnimatedPolygon points="0.5 179.5 42.5 179.5 42.5 1 0.5 1" />
                <AnimatedPath d="M56,139.6668 L56,179.6668 L68.913,179.6668 C73.336,179.6668 77.642,178.3008 81.268,175.7678 C94.483,166.5348 124,140.1508 124,88.6668 C124,34.0368 89.182,9.7018 76.896,2.8158 C74.389,1.4108 71.571,0.6668 68.696,0.6668 L55.667,0.6668 L55.667,40.3338 L73,40.3338 C73,40.3338 82.667,40.9998 82.667,57.9998 L82.667,128.9998 C82.667,128.9998 82.667,139.6668 72.667,139.6668 L56,139.6668 Z" />
            </AnimatedLetterMask>
        );
    }

    renderE() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('E')}
                maskWidth={117} maskHeight={180}
                startX={1118}
                backgroundX={-20} backgroundScale={0.5}>
                <AnimatedPolygon points="0.504310345 179.497207 42.8663793 179.497207 42.8663793 0 0.504310345 0" />
                <AnimatedPolygon points="54.9698276 42.2386592 116.141664 42.2386592 116.141664 0 54.9698276 0" />
                <AnimatedPolygon points="54.9698276 179.501229 116.141664 179.501229 116.141664 137.26257 54.9698276 137.26257" />
                <AnimatedPolygon points="40.7049052 113.132514 104.710966 113.132514 104.710966 70.8938547 40.7049052 70.8938547" />
            </AnimatedLetterMask>
        );
    }

    renderSS() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('SS')}
                maskWidth={124} maskHeight={180}
                startX={1255}
                backgroundX={50} backgroundY={-120} backgroundScale={0.65}>
                {this.renderSPath()}
            </AnimatedLetterMask>
        );
    }

    renderII() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('II')}
                maskWidth={42} maskHeight={180}
                backgroundX={10} backgroundScale={0.12}
                startX={1399}>
                {this.renderIPath()}
            </AnimatedLetterMask>
        );
    }

    renderG() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('G')}
                maskWidth={127} maskHeight={180}
                startX={1468}
                backgroundX={-20} backgroundY={10} backgroundScale={0.55}>
                <AnimatedPolygon points="64.988,119.507 125.164,119.507 125.164,77.735 64.988,77.735"/>
                <AnimatedPath d="M56.555,39.779V0.664c0,0-56.224-2.983-56.224,58.342v73.591c0,0,4.63,47.072,55.232,47.072v-40.11c0,0-14.552,0.994-14.552-13.592V55.691C41.01,55.691,39.688,39.779,56.555,39.779"/>
                <AnimatedPath d="M83.162,119.507v7.786c0,0-1.323,12.266-14.222,12.266v40.109c0,0,21.497,0,31.75-8.286c10.253-8.288,18.852-9.944,24.474-9.944v-41.931H83.162z"/>
                <AnimatedPath d="M69.205,40.193V0.912c0,0,51.594-5.221,57.547,52.458l-40.928,7.956C85.824,61.326,87.064,39.945,69.205,40.193"/>
            </AnimatedLetterMask>
        );
    }

    renderN() {
        return (
            <AnimatedLetterMask
                {...this.baseLetterProps('N')}
                maskWidth={126} maskHeight={180}
                startX={1615}
                backgroundX={10} backgroundY={-20} backgroundScale={0.2}>
                <AnimatedPolygon points="41.6692913 180.000298 0 180.000298 0 45.2489171 41.6692913 113.370464" />
                <AnimatedPolygon points="84.3308079 0.000397790055 84.3308079 54.6965304 126.000099 123.315315 126.000099 0.000397790055" />
                <AnimatedPolygon points="0.496062992 0.000397790055 0.496062992 22.3760884 42.1653543 90.9948729 95.0784094 179.83432 126 180.000398 126 151.657856 36.2125984 0.000397790055" />
            </AnimatedLetterMask>
        );
    }

    renderActive() {
        if (this.state.active) {
            return this[`render${this.state.active}`]();
        }
    }

    render() {
        let {width, height} = this.props;
        let ratio = viewboxWidth / width;

        let viewBox = `0 0 ${viewboxWidth} ${height * ratio}`;

        return (
            <svg className="svg" viewBox={viewBox}>
                {this.state.active !== 'S' && this.renderS()}
                {this.state.active !== 'H' && this.renderH()}
                {this.state.active !== 'I' && this.renderI()}
                {this.state.active !== 'V' && this.renderV()}
                {this.state.active !== 'A' && this.renderA()}

                {this.state.active !== 'D' && this.renderD()}
                {this.state.active !== 'E' && this.renderE()}
                {this.state.active !== 'SS' && this.renderSS()}
                {this.state.active !== 'II' && this.renderII()}
                {this.state.active !== 'G' && this.renderG()}
                {this.state.active !== 'N' && this.renderN()}
                {this.renderActive()}
            </svg>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/animated-letters.js